import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';
import { getAuthHeaders } from '../../utils/auth';
import { Search, Save, Zap, RefreshCcw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ApplicationEditor = () => {
  const { toast } = useToast();
  const [applicationId, setApplicationId] = useState('');
  const [application, setApplication] = useState(null);
  const [jsonValue, setJsonValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [forceSaving, setForceSaving] = useState(false);

  const loadApplication = async () => {
    const id = applicationId.trim();
    if (!id) {
      toast({ title: 'Enter Application ID', description: 'Please provide a valid application ID.', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/applications/${encodeURIComponent(id)}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        const error = await response.json().catch(() => null);
        throw new Error(error?.detail || 'Application not found');
      }
      const data = await response.json();
      const app = data.application;
      setApplication(app);
      setJsonValue(JSON.stringify(app, null, 2));
      toast({ title: 'Loaded', description: `Application ${id} loaded successfully.` });
    } catch (error) {
      setApplication(null);
      setJsonValue('');
      toast({ title: 'Load failed', description: error.message || 'Could not load application.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const saveApplication = async () => {
    if (!application) {
      toast({ title: 'No application loaded', description: 'Load an application first before saving.', variant: 'destructive' });
      return;
    }
    let payload;
    try {
      payload = JSON.parse(jsonValue);
    } catch (error) {
      toast({ title: 'Invalid JSON', description: 'Please fix JSON syntax before saving.', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/applications/${encodeURIComponent(application.applicationId)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const error = await response.json().catch(() => null);
        throw new Error(error?.detail || 'Failed to save application');
      }
      const data = await response.json();
      setApplication({ ...application, ...payload });
      setJsonValue(JSON.stringify({ ...application, ...payload }, null, 2));
      toast({ title: 'Saved', description: data.message || 'Application updated successfully.' });
    } catch (error) {
      toast({ title: 'Save failed', description: error.message || 'Could not save the application.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const forceCompletePayment = async () => {
    if (!application) {
      toast({ title: 'No application loaded', description: 'Load an application first to force payment.', variant: 'destructive' });
      return;
    }

    setForceSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/applications/${encodeURIComponent(application.applicationId)}/force-payment`, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        const error = await response.json().catch(() => null);
        throw new Error(error?.detail || 'Failed to force payment');
      }
      const data = await response.json();
      const updated = {
        ...application,
        paymentStatus: 'completed',
        paidAt: new Date().toISOString(),
        status: data.status || application.status
      };
      setApplication(updated);
      setJsonValue(JSON.stringify(updated, null, 2));
      toast({ title: 'Payment Forced', description: `Payment for ${application.applicationId} marked completed.` });
    } catch (error) {
      toast({ title: 'Force failed', description: error.message || 'Could not force payment.', variant: 'destructive' });
    } finally {
      setForceSaving(false);
    }
  };

  const resetEditor = () => {
    setApplication(null);
    setJsonValue('');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">Application Editor</CardTitle>
        <p className="text-sm text-gray-500 mt-1">
          Enter a valid application ID, load the record, edit fields directly in JSON, and save updates.
          Use force payment to mark the application as paid and bypass the normal payment step.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-[1.4fr_0.6fr] gap-4">
          <div className="space-y-3">
            <Label htmlFor="application-id">Application ID</Label>
            <div className="flex gap-3 items-center">
              <Input
                id="application-id"
                placeholder="APP20260408123456"
                value={applicationId}
                onChange={(e) => setApplicationId(e.target.value)}
              />
              <Button onClick={loadApplication} className="bg-blue-600 hover:bg-blue-700 text-white" disabled={loading}>
                <Search className="w-4 h-4 mr-2" />
                {loading ? 'Loading...' : 'Load'}
              </Button>
            </div>
          </div>
          <div className="space-y-3">
            <Label>Status</Label>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={saveApplication} disabled={!application || saving}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save JSON'}
              </Button>
              <Button variant="outline" onClick={forceCompletePayment} disabled={!application || forceSaving} className="border-orange-300 text-orange-700 hover:bg-orange-50">
                <Zap className="w-4 h-4 mr-2" />
                {forceSaving ? 'Forcing...' : 'Force Complete Payment'}
              </Button>
              <Button variant="ghost" onClick={resetEditor} disabled={!application}>
                <RefreshCcw className="w-4 h-4 mr-2" />
                Reset
              </Button>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <Label>Application JSON</Label>
            {application && (
              <p className="text-sm text-gray-500">
                Loaded: <span className="font-medium">{application.applicationId}</span>
              </p>
            )}
          </div>
          <Textarea
            value={jsonValue}
            onChange={(e) => setJsonValue(e.target.value)}
            rows={20}
            className="font-mono text-sm"
            placeholder="Load an application to edit its data..."
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default ApplicationEditor;
